import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn('[supabase] Faltan REACT_APP_SUPABASE_URL o REACT_APP_SUPABASE_ANON_KEY en .env');
}

export const supabase = createClient(SUPABASE_URL || 'https://example.supabase.co', SUPABASE_ANON_KEY || 'anon-key');

// Tipos de retorno para claridad
type UploadImageResult = { success: true; data: any; publicUrl: string } | { success: false; error: string };
type ListImagesResult = { success: true; images: { name: string; url: string; size: number; createdAt: string }[] } | { success: false; error: string; images: [] };
type DeleteImageResult = { success: true; data: any } | { success: false; error: string };

const BUCKET_NAME = 'assets';
const DEFAULT_FOLDER = 'products';
const SERVICE_CATEGORIES_FOLDER = 'service-categories';

export const supabaseStorage = {
  // Subir imagen al bucket
  uploadImage: async (file: File, fileName: string): Promise<UploadImageResult> => {
    try {
      const targetPath = fileName.includes('/') ? fileName : `${DEFAULT_FOLDER}/${fileName}`;
      console.debug('[supabaseStorage.uploadImage] bucket:', BUCKET_NAME, 'targetPath:', targetPath);
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(targetPath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: publicData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(targetPath);
      const publicUrl = publicData?.publicUrl;
      console.debug('[supabaseStorage.uploadImage] upload data:', data, 'publicUrl:', publicUrl);

      return {
        success: true,
        data,
        publicUrl: publicUrl || `${SUPABASE_URL}/storage/v1/object/public/${BUCKET_NAME}/${targetPath}`
      };
    } catch (error: any) {
      console.error('[supabaseStorage.uploadImage] Error uploading image:', error);
      const msg = deriveStorageErrorMessage(error);
      return { success: false, error: msg };
    }
  },

  // Obtener lista de imágenes del bucket (recursivo)
  listImagesDeep: async (path: string = ''): Promise<ListImagesResult> => {
    try {
      const startPath = path || DEFAULT_FOLDER;
      console.debug('[supabaseStorage.listImagesDeep] bucket:', BUCKET_NAME, 'startPath:', startPath);
      const items: { name: string; url: string; size: number; createdAt: string }[] = [];

      const listLevel = async (relativePath: string) => {
        try {
          const { data, error } = await supabase.storage
            .from(BUCKET_NAME)
            .list(relativePath, { limit: 100, offset: 0 });
          if (error) {
            console.warn('[supabaseStorage.listImagesDeep] list error at', relativePath || '/', error?.message || error);
            return [];
          }
          return data || [];
        } catch (err: any) {
          console.warn('[supabaseStorage.listImagesDeep] list exception at', relativePath || '/', err?.message || err);
          return [];
        }
      };

      const queue: string[] = [startPath];
      while (queue.length) {
        const current = queue.shift() as string;
        const entries = await listLevel(current);
        console.debug('[supabaseStorage.listImagesDeep] entries at', current || '/', entries);

        for (const entry of entries) {
          const asAny: any = entry as any;
          // Supabase FileObject no expone `type`.
          // Heurística para carpeta:
          // - id nulo/indefinido
          // - nombre sin punto y sin metadata.size
          const hasDot = (entry.name || '').includes('.');
          const hasSize = Number.isFinite(asAny?.metadata?.size);
          const isFolder =
            (asAny?.id === null || typeof asAny?.id === 'undefined') ||
            (!hasDot && !hasSize);

          if (isFolder) {
            const nextPath = current ? `${current}/${entry.name}` : entry.name;
            queue.push(nextPath);
            continue;
          }

          // archivo
          const nameLower = entry.name?.toLowerCase() || '';
          const extension = nameLower.split('.')?.pop();
          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '');
          if (isImage) {
            const fullPath = current ? `${current}/${entry.name}` : entry.name;
            const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(fullPath);
            const url = urlData?.publicUrl || `${SUPABASE_URL}/storage/v1/object/public/${BUCKET_NAME}/${fullPath}`;
            items.push({
              name: fullPath,
              url,
              size: (asAny?.metadata?.size as number) || 0,
              createdAt: (asAny?.created_at as string)
            });
          }
        }
      }

      console.debug('[supabaseStorage.listImagesDeep] collected items:', items);
      return { success: true, images: items };
    } catch (error: any) {
      console.error('[supabaseStorage.listImagesDeep] Error listing images deep:', error);
      const msg = deriveStorageErrorMessage(error);
      return { success: false, error: msg, images: [] };
    }
  },

  // Eliminar imagen del bucket
  deleteImage: async (path: string): Promise<DeleteImageResult> => {
    try {
      console.debug('[supabaseStorage.deleteImage] bucket:', BUCKET_NAME, 'path:', path);
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([path]);
      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[supabaseStorage.deleteImage] Error deleting image:', error);
      const msg = deriveStorageErrorMessage(error);
      return { success: false, error: msg };
    }
  },

  // Subir imagen de categoría de servicio
  uploadServiceCategoryImage: async (file: File, categoryName?: string): Promise<UploadImageResult> => {
    try {
      // Generar nombre único para el archivo
      const timestamp = Date.now();
      const sanitizedName = categoryName 
        ? categoryName.toLowerCase().replace(/[^a-z0-9]/g, '-') 
        : 'category';
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${sanitizedName}-${timestamp}.${extension}`;
      const targetPath = `${SERVICE_CATEGORIES_FOLDER}/${fileName}`;

      console.debug('[supabaseStorage.uploadServiceCategoryImage] bucket:', BUCKET_NAME, 'targetPath:', targetPath);
      
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(targetPath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: publicData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(targetPath);
      const publicUrl = publicData?.publicUrl;
      console.debug('[supabaseStorage.uploadServiceCategoryImage] upload data:', data, 'publicUrl:', publicUrl);

      return {
        success: true,
        data: { ...data, path: targetPath },
        publicUrl: publicUrl || `${SUPABASE_URL}/storage/v1/object/public/${BUCKET_NAME}/${targetPath}`
      };
    } catch (error: any) {
      console.error('[supabaseStorage.uploadServiceCategoryImage] Error uploading image:', error);
      const msg = deriveStorageErrorMessage(error);
      return { success: false, error: msg };
    }
  }
};

function deriveStorageErrorMessage(error: any): string {
  const message: string = error?.message || '';
  const status: number | undefined = error?.status || error?.statusCode;

  if (status === 404 || /not found/i.test(message)) {
    return `El bucket \"${BUCKET_NAME}\" no existe o la ruta es incorrecta.`;
  }
  if (status === 401 || status === 403 || /permission/i.test(message) || /authorized/i.test(message)) {
    return 'Permisos insuficientes: revisa que el bucket sea público o las políticas RLS.';
  }
  if (/CORS/i.test(message)) {
    return 'Problema de CORS al acceder a los recursos del bucket.';
  }
  return message || 'Error al interactuar con el almacenamiento de Supabase';
}

// Servicio de categorías via Supabase (para AdminPanel)
export const categoryService = {
  getAll: async (): Promise<Array<{ id: number; name: string; slug?: string }>> => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, slug')
        .order('name', { ascending: true });

      if (error) throw error;
      return (data || []) as Array<{ id: number; name: string; slug?: string }>;
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }
};

export default supabaseStorage;